import { memo, useCallback, useState } from "react";
import { formatTimecode } from "./formatTime";

interface BoundaryMarkerProps {
	position: number; // タイムライン上の相対位置 (0-1)
	timeSec: number;
	index: number;
	selected: boolean;
	timelineRef: React.RefObject<HTMLDivElement | null>;
	onMove: (index: number, relativeX: number) => void;
	onSelect: (index: number) => void;
	onContextMenu: (index: number, x: number, y: number) => void;
	onDragStart: (index: number) => void;
	onDragEnd: () => void;
}

export const BoundaryMarker = memo(function BoundaryMarker({
	position,
	timeSec,
	index,
	selected,
	timelineRef,
	onMove,
	onSelect,
	onContextMenu,
	onDragStart,
	onDragEnd,
}: BoundaryMarkerProps) {
	const [dragging, setDragging] = useState(false);

	const handlePointerDown = useCallback(
		(e: React.PointerEvent) => {
			if (e.button !== 0) return;
			e.preventDefault();
			e.stopPropagation();
			setDragging(true);
			onSelect(index);
			onDragStart(index);
			(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
		},
		[index, onSelect, onDragStart],
	);

	// 選択は Timeline 側 (onContextMenu) がメニュー表示とまとめて行う。
	const handleContextMenu = useCallback(
		(e: React.MouseEvent) => {
			e.preventDefault();
			e.stopPropagation();
			onContextMenu(index, e.clientX, e.clientY);
		},
		[index, onContextMenu],
	);

	const handlePointerMove = useCallback(
		(e: React.PointerEvent) => {
			if (!dragging || !timelineRef.current) return;
			const rect = timelineRef.current.getBoundingClientRect();
			const relX = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
			onMove(index, relX);
		},
		[dragging, index, onMove, timelineRef],
	);

	const handlePointerUp = useCallback(
		(e: React.PointerEvent) => {
			if (!dragging) return;
			setDragging(false);
			const target = e.currentTarget as HTMLElement;
			if (target.hasPointerCapture(e.pointerId)) target.releasePointerCapture(e.pointerId);
			onDragEnd();
		},
		[dragging, onDragEnd],
	);

	return (
		<div
			className={`absolute top-0 h-full cursor-col-resize ${dragging ? "z-30" : ""}`}
			style={{ left: `${position * 100}%`, transform: "translateX(-50%)", width: "16px" }}
			onPointerDown={handlePointerDown}
			onPointerMove={handlePointerMove}
			onPointerUp={handlePointerUp}
			onPointerCancel={handlePointerUp}
			onLostPointerCapture={handlePointerUp}
			onContextMenu={handleContextMenu}
			data-testid={`boundary-${index}`}
		>
			{dragging && (
				<div className="pointer-events-none absolute -top-6 left-1/2 z-30 -translate-x-1/2 whitespace-nowrap rounded-2 bg-neutral-solid px-1.5 py-0.5 text-section font-mono text-on-neutral-solid">
					{formatTimecode(timeSec, 1)}
				</div>
			)}
			<div className={`mx-auto h-full w-0.5 ${selected ? "bg-accent-solid" : "bg-fg-1"}`} />
			<div
				className={`absolute left-1/2 top-0 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 ${
					selected ? "border-accent-solid bg-surface-1" : "border-fg-1 bg-surface-1"
				}`}
			/>
		</div>
	);
});
